import { pick } from 'lodash-es';
import { parseJSON } from '@src/utils/index';
import { DEFAULT_CITY } from './constants';
import type { CityBase, CityListItem, CurrentCity, MyCityMapItem, SystemGeoItem } from './types';

/**
 * 随 myCityMapList 下发的会员及扩展字段。
 * 城市已不在 myCityMapList 中时需一并清理，避免本地残留过期的在期状态。
 */
export const CITY_VIP_FIELDS: (keyof CityBase)[] = ['vipStatus', 'vipVersion', 'vipEndTime', 'vipStartTime'];

/** 判断是否为有效的 [lng, lat] 或 [x, y] 数值对 */
export const isCoordinatePair = (value: unknown): value is [number, number] =>
    Array.isArray(value) && value.length >= 2 && Number.isFinite(Number(value[0])) && Number.isFinite(Number(value[1]));

export const parseCentroidLatlon = (centroidGeo?: string): [number, number] => {
    const coordinates = parseJSON(centroidGeo ?? '')?.coordinates;
    return isCoordinatePair(coordinates) ? [Number(coordinates[0]), Number(coordinates[1])] : [...DEFAULT_CITY.latlon];
};

const resolveLatlon = (source: { latlon?: unknown; centroidGeo?: string }): [number, number] => {
    if (isCoordinatePair(source.latlon)) {
        return [Number(source.latlon[0]), Number(source.latlon[1])];
    }
    return parseCentroidLatlon(source.centroidGeo);
};

/**
 * 写入 currentCity 前的输入。
 * 兼容已标准化的 CityListItem / CurrentCity，以及定位、持久化等松散字段。
 */
export type CurrentCityInput = Partial<CityBase> &
    Partial<CurrentCity> & {
        centroidGeo?: string;
        areaName?: string;
    };

/**
 * 标准化 geo/list 单条记录。
 * 接口 id 实际为 areaId；mapId 为地图业务主键。
 */
export const normalizeSystemGeoItem = (raw: SystemGeoItem): CityListItem => ({
    id: raw.mapId,
    mapId: raw.mapId,
    areaId: raw.id,
    name: raw.name,
    latlon: parseCentroidLatlon(raw.centroidGeo),
    firstWord: raw.firstWord,
});

/**
 * 标准化 myCityMapList 单条记录。
 * 接口 id 即 mapId；areaId 为行政区划 id。
 * 已开通城市额外保留 VIP 相关字段。
 */
export const normalizeMyCityMapItem = (raw: MyCityMapItem): CityListItem => ({
    id: raw.id,
    mapId: raw.id,
    areaId: raw.areaId,
    name: raw.name,
    latlon: parseCentroidLatlon(raw.centroidGeo),
    vipStatus: raw.vipStatus,
    vipVersion: raw.vipVersion,
    vipEndTime: raw.vipEndTime,
    vipStartTime: raw.vipStartTime,
});

/** 批量标准化 geo/list 返回值 */
export const normalizeSystemGeoList = (list: SystemGeoItem[] = []) => list.map(normalizeSystemGeoItem);

/** 批量标准化 myCityMapList 返回值 */
export const normalizeMyCityMapList = (list: MyCityMapItem[] = []) => list.map(normalizeMyCityMapItem);

/** 列表项转当前城市上下文（去除 firstWord） */
export const cityListItemToCurrentCity = (city: CityListItem): CurrentCity => {
    const { firstWord: _firstWord, ...currentCity } = city;
    return currentCity;
};

/**
 * 将接口/定位/持久化等原始或半标准化数据规范为 CurrentCity。
 * VIP 字段按源数据原样透传，是否携带由调用方决定（会员信息统一以 myCityMapList 为准）。
 * automaticPositioning 仅标记城市来源，不参与 VIP 字段处理。
 */
export const normalizeCurrentCity = (raw: CurrentCityInput = {}): CurrentCity => {
    const source = raw && typeof raw === 'object' ? raw : {};
    const baseCity: CityBase = {
        id: source.id ?? source.mapId ?? DEFAULT_CITY.id,
        mapId: source.mapId ?? source.id ?? DEFAULT_CITY.mapId,
        name: source.name ?? source.areaName ?? DEFAULT_CITY.name,
        areaId: source.areaId ?? DEFAULT_CITY.areaId,
        latlon: resolveLatlon(source),
    };

    return {
        ...baseCity,
        ...pick(source, CITY_VIP_FIELDS),
        ...(source.automaticPositioning ? { automaticPositioning: true } : {}),
    };
};
